import {
  Button,
  Drawer,
  Form,
  Input,
  Row,
  notification,
  Upload,
  UploadFile,
} from 'antd'
import './DrawerPhotoComment.style.less'
import { v4 as uuidv4 } from 'uuid'

import {
  DoubleRightOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import type { RcFile, UploadProps } from 'antd/es/upload'
import { startCase, camelCase, isEmpty, size } from 'lodash'
import { useEffect, useState } from 'react'
import { useMutation } from 'react-query'
import {
  saveSectionRef,
  taskDetailRef,
  updatePhotoCommentRef,
  uploadInspectionTaskPhoto,
  deletePhotoComment,
  addNewPhotoCommentRef,
  defectDetailRef,
} from '../../InSpectionReport.service'
import { queryClient } from '@/config/react-query'
import ModalConfirm from '@/components/ModalConfirm/ModalConfirm'
import { InspectionReportSectionEnum } from '@/constant/qsf'

interface Props {
  onClose: () => void
  open: boolean
  data: any
  index: number
  photoIndex: number
  isEditPhoto: boolean
  type: InspectionReportSectionEnum
}

interface FileListType extends UploadFile {
  originalImage?: string
}

function DrawerPhotoComment(props: Props) {
  const [fileList, setFileList] = useState<FileListType[]>([])
  const onFinish = () => {}
  const [form] = Form.useForm()

  const [isChange, setIsChange] = useState(false)
  const [modalDeleteDefect, setModalDeleteDefect] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [commentTmp, setCommentTmp] = useState('')
  const [sectionType, setSectionType] =
    useState<InspectionReportSectionEnum | null>(null)
  const [mouseOver, setMouseOver] = useState(false)

  const getBase64 = (file: RcFile): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result as string)

      reader.onerror = (error) => reject(error)
    })

  const normFile = (e: any) => {
    setFileList(e.fileList)
    if (Array.isArray(e)) {
      return e
    }
    return e?.fileList
  }

  const handleSetFileList = async (propsData) => {
    const { values } = propsData
    if (!isEmpty(values)) {
      const valueMedia = values[props.photoIndex]
      const getFileName = valueMedia.media?.split('/') || ''
      const sliceFileName = isEmpty(getFileName)
        ? ''
        : getFileName[getFileName.length - 1].split('.').slice(0, -1).join('.')
      const tempId = uuidv4()

      const getImage: FileListType[] = [
        {
          uid: tempId,
          name: sliceFileName,
          status: 'done',
          url: valueMedia.thumbnail || valueMedia.media,
          originalImage: valueMedia.media,
        },
      ]

      form.setFieldsValue({
        comment: valueMedia?.comment,
        photo: getImage,
      })
      setCommentTmp(valueMedia?.comment)
      setFileList(getImage)
    }
  }

  useEffect(() => {
    setIsOpen(props.open)
  }, [props.open])

  useEffect(() => {
    setSectionType(props.type)
  }, [props.type])

  const handleStartComponent = () => {
    if (isOpen) {
      if (!props.isEditPhoto) {
        form.setFieldsValue({
          comment: '',
          photo: [],
        })
        setFileList([])
      }
    } else {
      form.resetFields()
      setFileList([])
    }
  }

  useEffect(() => {
    handleFetchData()
    handleStartComponent()
    if (!isOpen) {
      setIsChange(false)
    }
  }, [isOpen])

  const handleControlType = async (
    newThumbnailId = null,
    newImageId = null
  ) => {
    const formValue = form.getFieldsValue()
    try {
      if (sectionType === InspectionReportSectionEnum.CHECKPOINT) {
        const { data: taskDetailData } = await taskDetailRef(props.data?.id)
        const { originalId, thumbnailId } =
          taskDetailData.json_value[props.photoIndex]

        // DELETE/EDIT CHECKPOINT
        if (modalDeleteDefect) {
          await deletePhotoComment(props.data?.id, originalId, sectionType)
        } else {
          const payload = {
            thumbnailId: newThumbnailId || thumbnailId,
            comment: formValue.comment,
            mediaId: newImageId,
            originalId,
            type: sectionType,
          }
          await updatePhotoCommentRef(props.data?.id, payload)
        }
      }

      if (sectionType === InspectionReportSectionEnum.DEFECT) {
        const { data } = await defectDetailRef(props.data?.id)
        const { originalId, thumbnailId } = data.medias[props.photoIndex]

        // DELETE/EDIT CHECKPOINT
        if (modalDeleteDefect) {
          await deletePhotoComment(props.data?.id, originalId, sectionType)
        } else {
          const payload = {
            originalId,
            mediaId: newImageId,
            thumbnailId: newThumbnailId || thumbnailId,
            comment: formValue.comment,
            type: sectionType,
          }
          await updatePhotoCommentRef(props.data?.id, payload)
        }
      }
    } catch (error) {
      throw error
    }
  }

  const handleUploadPicture = async (baseObjectPicture) => {
    const data_raw = await getBase64(baseObjectPicture.originFileObj)
    const imageName = uuidv4()
    const thumbUUID = uuidv4()
    const payload = {
      media_id: imageName,
      reference_id: props.data.id,
      table: 'inspections',
      name: imageName,
      data_raw: data_raw,
      type: baseObjectPicture.type,
      size: `${baseObjectPicture.size}`,
    }
    const payloadThumbnail = {
      media_id: thumbUUID,
      reference_id: props.data.id,
      table: 'inspections',
      name: `thumbnail-${thumbUUID}`,
      data_raw: baseObjectPicture.thumbUrl,
      type: baseObjectPicture.type,
      size: `${baseObjectPicture.size}`,
    }
    const [orgImageData, thumbImageData] = await Promise.all([
      uploadInspectionTaskPhoto(payload),
      uploadInspectionTaskPhoto(payloadThumbnail),
    ])

    return { orgImageData, thumbImageData }
  }

  const formPhotoValidation = () => {
    if (isEmpty(fileList)) {
      form.setFields([
        {
          name: 'photo',
          errors: ['Photo is required!'],
        },
      ])
      throw new Error('Photo is required!')
    }
  }

  const onSave = useMutation(async () => {
    try {
      const { photo: formPhoto, comment: formComment } = form.getFieldsValue()
      const { isEditPhoto } = props
      formPhotoValidation()

      let newImageId = null
      let newThumbnailId = null

      if (isEditPhoto) {
        if (!isEmpty(formPhoto[0].originFileObj)) {
          const { orgImageData, thumbImageData } = await handleUploadPicture(
            formPhoto[0]
          )
          newImageId = orgImageData.id
          newThumbnailId = thumbImageData.id
        }
        await handleControlType(newThumbnailId, newImageId)
      } else {
        const { orgImageData, thumbImageData } = await handleUploadPicture(
          formPhoto[0]
        )
        const payload = {
          comment: formComment,
          originalId: orgImageData.id,
          thumbnailId: thumbImageData.id,
          type: sectionType,
          key: `${size(props.data?.values)}`,
        }

        await addNewPhotoCommentRef(props.data?.id, payload)
      }

      await queryClient.invalidateQueries(['GET_REPORT_DATA_HEADER'])
      await queryClient.invalidateQueries(['GET_REPORT_DATA'])
      props.onClose()
      setIsChange(false)
    } catch (error: any) {
      if (error) {
        notification.error({
          message: error.message,
          // ? error?.errorFields[0]?.errors[0]
          // : error.response?.data?.message,
          duration: 4,
          placement: 'top',
        })
      }
    }
  })

  const handlePreview = async () => {
    let src = fileList[0].originalImage as string
    if (!src) {
      src = await new Promise((resolve) => {
        const reader = new FileReader()
        reader.readAsDataURL(fileList[0].originFileObj as RcFile)
        reader.onload = () => resolve(reader.result as string)
      })
    }
    const image = new Image()
    image.src = src
    const imgWindow = window.open(src)
    imgWindow?.document.write(image.outerHTML)
  }

  const handleImageChange: UploadProps['onChange'] = ({
    fileList: newFileList,
  }) => {
    setFileList(newFileList)
    setIsChange(true)
  }

  const deleteMutation = useMutation(async () => {
    await handleControlType()
  })

  const onDelete = async () => {
    await deleteMutation.mutateAsync()
    setModalDeleteDefect(false)
    await queryClient.invalidateQueries(['GET_REPORT_DATA_HEADER'])
    await queryClient.invalidateQueries(['GET_REPORT_DATA'])
    props.onClose()
  }

  const handleFetchData = async () => {
    await handleSetFileList(props.data)
  }

  useEffect(() => {
    const isOpenSelect = document.querySelector('.ant-select-open')
    const allowScrolling = !mouseOver && !isOpenSelect
    if (allowScrolling) {
      document.body.style.overflow = 'auto'
    } else {
      document.body.style.overflow = ''
    }
  }, [mouseOver])

  const defectName = [
    `${props.data?.code}`,
    `${startCase(camelCase(props.data.name))}`,
  ].join(' - ')

  const handleFormChange = () => {
    const { photo, comment } = form.getFieldsValue()
    const formItemCommentChange =
      comment !== commentTmp || !photo[0]?.originalImage
    setIsChange(formItemCommentChange)
  }

  return (
    <Drawer
      title=""
      placement={'right'}
      closable={false}
      open={isOpen}
      key={'right'}
      width={400}
      onMouseOver={() => {
        setMouseOver(true)
      }}
      onMouseLeave={() => setMouseOver(false)}
    >
      <div className="drawer-photo-container">
        <Button
          onClick={props.onClose}
          className="button-close"
          disabled={onSave.isLoading || deleteMutation.isLoading}
          icon={<DoubleRightOutlined className="close-icon" />}
        />
        {props.isEditPhoto && (
          <Row align={'middle'} className="title-container">
            <EditOutlined />
            <p>Edit Photo</p>
          </Row>
        )}

        {!props.isEditPhoto && (
          <Row align={'middle'} className="title-container">
            <PlusOutlined />
            <p>Add Photo</p>
          </Row>
        )}

        <p className="section-name">{props?.index}</p>
        <p className="section-subtitle-name">
          {props?.data?.title ?? defectName}
        </p>
        <Form
          form={form}
          onFinish={onFinish}
          autoComplete="off"
          onChange={handleFormChange}
          layout="vertical"
        >
          <Form.Item
            label="Photo"
            name={'photo'}
            rules={[{ required: true, message: 'Photo is required!' }]}
            getValueFromEvent={normFile}
            valuePropName="fileListOrder"
          >
            <Upload
              className="edit-photo-item"
              listType="picture-card"
              maxCount={1}
              accept="image/png, image/jpeg, image/jpg, image/bmp, image/webp"
              disabled={onSave.isLoading || deleteMutation.isLoading}
              fileList={fileList}
              method={'GET' as any}
              beforeUpload={() => false}
              onPreview={handlePreview}
              onChange={handleImageChange}
            >
              {fileList.length > 0 ? null : '  + Upload'}
            </Upload>
          </Form.Item>
          <Form.Item label="Comment" name={'comment'}>
            <Input.TextArea
              disabled={onSave.isLoading || deleteMutation.isLoading}
              autoSize
              size="large"
              placeholder="Comment"
            />
          </Form.Item>
        </Form>
        <Row className="button-container">
          {props.isEditPhoto && (
            <Button
              onClick={() => setModalDeleteDefect(true)}
              size="large"
              className="delete"
              disabled={onSave.isLoading || deleteMutation.isLoading}
              icon={<DeleteOutlined />}
            />
          )}

          <Button
            disabled={onSave.isLoading || deleteMutation.isLoading}
            onClick={props.onClose}
            className="cancel"
            size="large"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSave.mutateAsync()}
            size="large"
            type="primary"
            disabled={onSave.isLoading || deleteMutation.isLoading || !isChange}
            loading={onSave.isLoading}
          >
            Save
          </Button>
        </Row>
      </div>
      {modalDeleteDefect && (
        <ModalConfirm
          content={
            <div>
              Are you sure you want to delete this photo? You won’t be able to
              undo this action.
            </div>
          }
          onClose={() => setModalDeleteDefect(false)}
          title="Delete Photo"
          open={modalDeleteDefect}
          onOK={async () => await onDelete()}
          onCancel={() => setModalDeleteDefect(false)}
          cancelButtonText="No, Cancel"
          type="delete"
          confirmButtonText="Yes, Delete"
        />
      )}
    </Drawer>
  )
}

export default DrawerPhotoComment
